// src/services/iomtApiService.js
import { apiConfig } from '../config/apiConfig';

class IomtApiService {
  // Save IoMT data from a device
  async saveIomtData(userId, deviceData, country = 'United States') {
    try {
      const url = `${apiConfig.backendUrl}${apiConfig.endpoints.iomt.save(userId)}?code=${apiConfig.functionKeys.iomtDataSave}`;
      
      // Add local time headers
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
        'X-Local-Year': new Date().getFullYear().toString(),
        'X-Local-Month': String(new Date().getMonth() + 1).padStart(2, '0'),
        'X-Local-Day': String(new Date().getDate()).padStart(2, '0'),
        'X-Local-Hour': String(new Date().getHours()).padStart(2, '0'),
        'X-Local-Minute': String(new Date().getMinutes()).padStart(2, '0')
      };
      
      // Prepare the data in the expected format
      const iomtData = {
        userId: userId,
        country: country,
        data: {
          metadata: {
            deviceId: deviceData.deviceId,
            deviceType: deviceData.deviceType,
            timestamp: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            dataVersion: '1.0'
          },
          metrics: deviceData.metrics,
          deviceContext: {
            batteryLevel: deviceData.batteryLevel,
            firmwareVersion: deviceData.firmwareVersion,
            lastSync: new Date().toISOString()
          }
        }
      };
      
      console.log('Saving IoMT data:', iomtData);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(iomtData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save IoMT data: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('IoMT data saved successfully:', result);
      return result;
    } catch (error) {
      console.error('Error saving IoMT data:', error);
      throw error;
    }
  }

  // Get IoMT data for a user
  async getIomtData(userId, options = {}) {
  try {
    const {
      deviceType = null,
      deviceId = null,
      startDate = null,
      endDate = null,
      metricType = null,
      limit = 100,
      aggregation = null,
      onlyLatest = false
    } = options;
    
    // Build query parameters
    const params = new URLSearchParams({
      code: apiConfig.functionKeys.iomtDataGet || apiConfig.functionKeys.iomtDataSave
    });
    
    // Make sure ALL parameters are added
    if (deviceType) params.append('deviceType', deviceType);
    if (deviceId) params.append('deviceId', deviceId);
    if (startDate) params.append('startDate', startDate);  // THIS IS IMPORTANT
    if (endDate) params.append('endDate', endDate);        // THIS IS IMPORTANT
    if (metricType) params.append('metricType', metricType);
    if (limit) params.append('limit', limit.toString());
    if (aggregation) params.append('aggregation', aggregation);
    if (onlyLatest) params.append('onlyLatest', 'true');
    
    const url = `${apiConfig.backendUrl}${apiConfig.endpoints.iomt.get(userId)}?${params.toString()}`;
    
    console.log('📡 IOMT API: Fetching data from:', url);
    console.log('📡 IOMT API: Query params:', params.toString());
    console.log('📡 IOMT API: Date range:', startDate, 'to', endDate);
    console.log('📡 IOMT API: Record limit:', limit);
    
    // Get auth token
    const authToken = localStorage.getItem('authToken') || '';
    console.log('Auth token exists:', !!authToken);
    console.log('Auth token preview:', authToken ? authToken.substring(0, 20) + '...' : 'NO TOKEN');
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    };
    
    console.log('Request headers:', headers);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: headers
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`Failed to fetch IoMT data: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('📡 IOMT API: Response received:', data);
    
    // Debug: Show date range of received data
    if (data?.data?.length > 0) {
      const dates = data.data.map(record => record._metadata?.fileDate).filter(Boolean);
      const uniqueDates = [...new Set(dates)].sort();
      console.log(`📡 IOMT API: Received ${data.data.length} records covering ${uniqueDates.length} days`);
      console.log('📡 IOMT API: Date range in response:', uniqueDates[0], 'to', uniqueDates[uniqueDates.length - 1]);
      console.log('📡 IOMT API: All dates:', uniqueDates);
    } else {
      console.log('📡 IOMT API: No data received');
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching IoMT data:', error);
    throw error;
  }
}

  // Save HealthKit data in the correct format
  async saveHealthKitData(userId, healthKitData, country = 'United States') {
    const deviceData = {
      deviceId: healthKitData.deviceId || 'iphone-healthkit',
      deviceType: 'iphone-healthkit',
      metrics: {
        vitalSigns: healthKitData.vitalSigns || {},
        activity: healthKitData.activity || {},
        lifestyle: healthKitData.lifestyle || {},
        nutrition: healthKitData.nutrition || {}
      },
      batteryLevel: 100,
      firmwareVersion: healthKitData.appVersion || '1.0.0'
    };
    
    return this.saveIomtData(userId, deviceData, country);
  }

  // Save Apple Watch data
  async saveAppleWatchData(userId, healthData, country) {
    const deviceData = {
      deviceId: healthData.deviceId || 'apple-watch-default',
      deviceType: 'apple-watch',
      metrics: {
        heartRate: {
          value: healthData.heartRate,
          unit: 'bpm',
          context: healthData.context || 'resting'
        },
        steps: {
          value: healthData.steps,
          unit: 'count',
          period: 'daily'
        },
        activeCalories: {
          value: healthData.activeCalories,
          unit: 'kcal'
        }
      },
      batteryLevel: healthData.batteryLevel || 100,
      firmwareVersion: healthData.firmwareVersion || 'watchOS 10.0'
    };
    
    return this.saveIomtData(userId, deviceData, country);
  }

  // Save Fitbit data
  async saveFitbitData(userId, fitbitData, country) {
    const deviceData = {
      deviceId: fitbitData.deviceId || 'fitbit-default',
      deviceType: 'fitbit',
      metrics: {
        heartRate: {
          value: fitbitData.heartRate?.value,
          unit: 'bpm',
          context: 'average'
        },
        steps: {
          value: fitbitData.steps,
          unit: 'count',
          period: 'daily'
        },
        sleepScore: {
          value: fitbitData.sleepScore,
          unit: 'score',
          max: 100
        }
      },
      batteryLevel: fitbitData.battery || 100,
      firmwareVersion: fitbitData.deviceVersion || 'Fitbit OS 6.0'
    };
    
    return this.saveIomtData(userId, deviceData, country);
  }

  // Get aggregated data for a specific health plan
  async getHealthPlanMetrics(userId, planType, dateRange, country) {
    const options = {
      country: country,
      startDate: dateRange.start,
      endDate: dateRange.end,
      aggregation: 'daily'
    };
    
    const data = await this.getIomtData(userId, options);
    return this.processDataForPlan(data, planType);
  }

  // Process data for specific health plans
  processDataForPlan(rawData, planType) {
    const planMetrics = {
      'weight-loss': ['steps', 'activeCalories', 'weight', 'exerciseMinutes'],
      'diabetes': ['bloodGlucose', 'steps', 'mealTiming', 'medication'],
      'heart-health': ['heartRate', 'bloodPressure', 'exerciseMinutes', 'restingHeartRate']
    };
    
    return rawData;
  }
}

// Export singleton instance
const iomtApiService = new IomtApiService();
export default iomtApiService;